// 3rd-party modules
import moment from "moment-timezone";
import { Tooltip, message } from "antd";
import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";

// project modules
import AccountDeviceMenuModal from "./AccountDeviceMenuModal";
import AccountDeviceModal from "./AccountDeviceModal";
import Button from "../../shared/button";
import ConfirmationPopup from "../../shared/popup/confirmationPopup";
import ContextMenu from "../../shared/contextMenu/contextMenu";
import DataTable, { TableColumn } from "../../shared/list/dataTable";
import ListView from "../../shared/list/listView";
import Loader from "../../shared/loader";
import Popup from '../../shared/popup/popup';
import Select from "../../shared/inputs/select";
import { apiCall } from "../../../helpers/apiHelper";
import { CONSTANTS } from "../../../helpers/defines";
import { getDeviceSignalRateClassIcon } from "../../../helpers";
import { ListViewType } from "../../shared/list/useListHook";

// apis
import * as AccountDeviceApi from '../../../apis/accountDeviceApi';
import * as AccountKiboshDeviceApi from '../../../apis/accountKiboshDeviceApi';
import * as DashApi from '../../../apis/dashApi';
import * as CommonValueApi from '../../../apis/commonValueApi';
import * as AuthApi from "../../../apis/authApi";


// models
import { Account } from "../../../models/account";
import { AccountDevice } from "../../../models/accountDevice";
import AccountDeviceMalwareLogs from '../device-malware-logs/AccountDeviceMalwareLogs';
import AccountDeviceExplicitLogs from '../device-explicit-logs/AccountDeviceExplicitLogs';
import { AccountKiboshDevice } from "../../../models/accountKiboshDevice";
import { DataSourceRequest } from "../../../models/dataSourceRequest";
import { AccountDeviceGroup } from "../../../models/accountDeviceGroup";
import { AccountKiboshVpnDevice } from "../../../models/accountKiboshVpnDevice";
import { CommonValue } from "../../../models/commonValue";

//
import FiltersSvg from '../../../assets/icons/settings-adjust-carbon.svg';
import VpnSvg from '../../../assets/icons/vpn.svg';


type Props = {
  account?: Account;
  accountKiboshDevice?: AccountKiboshDevice;
  refresh?: boolean;
  malwareDevicesOnly?: boolean;
  explicitDevicesOnly?: boolean;
  onCancel?: () => void;
  onSave?: (account: Account) => void;
  onGroupSave?: () => void;
};

export default function AccountDevices({ account, refresh, accountKiboshDevice, malwareDevicesOnly = false, explicitDevicesOnly = false, onCancel, onSave, onGroupSave }: Props) {
  const columns: TableColumn<any>[] = [
    {
      title: 'MAC ID',
      dataIndex: 'id',
      key: 'id',
      filterable: !isMobile,
      width: 150,
      render: (text:any, record:any) => (
        <text data-color={ record.connectionStatus == "offline" ? "grey" : "black" } data-weight={ record.connectionStatus == "offline" ? "300" : "900" }>{text}</text>
      )
    },
    {
      title: 'Profile',
      dataIndex: 'groupName',
      key: 'groupName',
      filterable: !isMobile,
      width: 120,
      render: (text: any, record:any) => (
        
            <text data-color={ record.groupColor ? record.groupColor : (record.connectionStatus == "offline" ? "grey" : "black") } data-weight={record.connectionStatus == "offline" ? "300" : "900"} >{text}</text>
        )
    },
    {
      title: 'Device Name',
      dataIndex: 'name',
      key: 'name',
      filterable: !isMobile,
      render: (text: any, record: any) => (
        <group style={{minWidth: "110px" }}>
            <text data-color={ record.connectionStatus == "offline" ? "grey" : "black" } data-weight={record.connectionStatus == "offline" ? "300" : "900"} >{text}</text>
            {!!record.numSchedules &&
              <Tooltip title={`${record.numSchedules} Schedules`}><div className='button micro' onClick={record.groupId ? undefined : () => onSelectedTab(record, "internet-schedules")}>
                {record.numActiveSchedules == 0 ?
                  <icon>schedule</icon>
                  :
                  <icon data-icon-style="circle" data-background="red" data-color="white" >schedule</icon>
                }
              </div></Tooltip>
            }
            { (record.numSetFilters + record.numCustomFilters > 0 ) &&
              <Tooltip title={`${ record.numSetFilters + record.numCustomFilters  } Filters`}>
                <div className='button micro' onClick={record.groupId ? undefined : () => onSelectedTab(record, "blocked-sites")}>
                  <icon>page_info</icon>
                </div>
              </Tooltip>
            }
        </group>)
    },
    {
      title: "Inet",
      key: 'inet',
      width: 75,
      render: (_, record: any) => (
        <>
          <text 
            data-weight={ record.numActiveSchedules > 0 || record.isBlocked ? "900" : "300"}  
            data-color={ record.numActiveSchedules > 0 || record.isBlocked ? ( record.connectionStatus == "offline" ? "grey" : "black" ) : "light-gray"}
            style={{ marginRight: "8px" }}
          >
            { record.numActiveSchedules > 0 || record.isBlocked ? "OFF" : "ON" }
          </text>
          { record.numActiveSchedules > 0 && <icon data-icon-style="circle" data-background="red" data-color="white" >schedule</icon> }
          { record.isBlocked > 0 && <icon data-icon-style="circle" data-background="red" data-color="white" >pause_circle</icon> }
        </>
      )
    },


    {
      title: !isMobile ? 'Connection' : 'Conn.',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      filterable: !isMobile,
      width: 110,
      dataType: 'string',
      render: (text: any, record: any) => ( record.connectionStatus == "remote" && record.vpnLongitude != null && record.vpnLatitude != null ?
        <a href={"https://maps.google.com/?q=" + record.vpnLatitude + "," + record.vpnLongitude } target="_blank" data-weight="900" data-color="main" style={{ textDecoration: "underline" }}>{ getStatus(record) }</a>
        :
	<text data-color={ record.connectionStatus == "offline" ? "grey" : "black" } data-weight={record.connectionStatus == "offline" ? "300" : "900"} >{ getStatus(record) }</text>
        )
    },
    {
      title: 'Device IP',
      dataIndex: 'ip',
      key: 'ip',
      filterable: !isMobile,
      width: 110,
      render: (text:any, record:any) => (
        <text data-color={ record.connectionStatus == "offline" ? "grey" : "black" } data-weight={ record.connectionStatus == "offline" ? "300" : "900" }>{text}</text>
      )


    },
    {
      title: 'Static IP',

      dataIndex: 'staticIp',
      key: 'staticIp',
      filterable: !isMobile,
      width: 110,
      render: (text:any, record:any) => (
        <text data-color={ record.connectionStatus == "offline" ? "grey" : "black" } data-weight={ record.connectionStatus == "offline" ? "300" : "900" }>{text}</text>
      )

    },
    {
      title: !isMobile ? 'Signal' : 'Sig.',
      key: 'connectionQuality',
      width: 110,
      render: (_, record: any) => (
      <>
        { (!(!!record.isVpn && !!record.vpnIsConnected)) && <icon data-color={ record.connectionStatus == "offline" ? "grey" : "black" } >{getDeviceSignalRateClassIcon(record.pingMs!)}</icon> }
        {   (!!record.isVpn && !!record.vpnIsConnected)  && <img data-icon-size="24" data-position="center" src={ VpnSvg } /> }
      </>
      )
    },
/*
    {
      title: !isMobile ? 'Connection' : 'C.',
      dataIndex: 'connectionStatus',
      key: 'connectionStatus',
      width: 130,
      render: (text:any, record:any) => (
        <text data-color={ record.connectionStatus == "offline" ? "grey" : "black" } data-weight={ record.connectionStatus == "offline" ? "300" : "900" }>{text}</text>
      )

    },
*/
    {
      title: !isMobile ? 'Malware' : 'Mal.',
      key: 'malwares',
      width: 75,
      render: (_, record: any) => (
          <text 
                onClick={ () => onMalwareCountClick(record) }
                data-color={ record.detectedMalware?.length > 0 ? "red" : ( record.connectionStatus == "offline" ? "grey" : "black" ) } 
                data-weight={ record.connectionStatus == "offline" ? "300" : "900"} 
          >
            {record.detectedMalware?.length}
          </text>
      )
    },
    {
      title: !isMobile ? 'Explicit' : 'Expl.',
      key: 'malwares',
      width: 75,
      render: (_, record: any) => (
          <text 
            onClick={ () => onExplicitCountClick(record) }
            data-color={ record.detectedExplicit?.length > 0 ? "red" : (record.connectionStatus == "offline" ? "grey" : "black") }
            data-weight={ record.connectionStatus == "offline" ? "300" : "900"} 
          >
            {record.detectedExplicit?.length}
          </text>
      )
    },

    {
      title: !isMobile ? 'VPN' : 'V.',
      key: 'vpn',
      width: 60,
      render: (_, record: any) => (
        <>
          {!!record.isVpn &&
              <text data-color={ record.connectionStatus == "offline" ? "grey" : "black" } data-weight={ record.connectionStatus == "offline" ? "300" : "900"} >{!!record.vpnIsConnected ? "On" : "Off"}</text>
          }
        </>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 30,
      render: (_, record: any) => (
        <group data-wrap='no' data-gap="5">
          <Tooltip title="Edit"><div className='button micro' onClick={() => onEditClick(record)}><icon>edit_square</icon></div></Tooltip>
        </group>
      ),
    }
  ];
  const [accountKiboshDevices, setAccountKiboshDevices] = useState<AccountKiboshDevice[]>([]);
  const [currentAccountKiboshDevice, setCurrentAccountKiboshDevice] = useState<AccountKiboshDevice | null>(null);
  const [currentAccountDevice, setCurrentAccountDevice] = useState<AccountDevice>(new AccountDevice());
  const [accountKiboshVpnDevices, setAccountKiboshVpnDevices] = useState<AccountKiboshVpnDevice[]>([]);
  const [devicesDataSource, setDevicesDataSource] = useState<any[]>([]);
  const [isDeleteConfirmationPopupOpen, setIsDeleteConfirmationPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingAccountKiboshDevices, setLoadingAccountKiboshDevices] = useState(false);
  const [preSelectedTab, setPreSelectedTab] = useState<string>("");
  const [reload, setReload] = useState<boolean>(false);
  const [showAccountDeviceModal, setShowAccountDeviceModal] = useState(false);
  const [advanceMode, setAdvanceMode] = useState(!isMobile);
  const [showAccountDeviceMenuModal, setShowAccountDeviceMenuModal] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);

  const [explicitPopupOpen, setExplicitPopupOpen] = useState<boolean>(false)
  const [malwarePopupOpen, setMalwarePopupOpen]   = useState<boolean>(false)


  let abortController = new AbortController();

  useEffect( () => {
    const interval = setInterval(() => {
      setReload(true);
    }, 120*1000);

    return () => clearInterval(interval); 
  },[]);

  useEffect(() => {
    if (reload) {
      getDataAsync();
      setReload(false);
    }
  }, [reload]);

  useEffect(() => {
    if (refresh) {
      if (!accountKiboshDevice?.kiboshDeviceReference) {
        getAccountKiboshDevicesAsync();
      } else {
        setReload(true);
      }
    }
  }, [refresh]);

  useEffect(() => {
    setReload(true);
  }, [accountKiboshDevice, currentAccountKiboshDevice]);

  const getAccountKiboshDevicesAsync = async () => {
    setAccountKiboshDevices(await getAccountKiboshDevices({}, abortController.signal));
  }

  const getAccountKiboshDevices = async (request?: DataSourceRequest, abortSignal?: AbortSignal) => {
    setLoadingAccountKiboshDevices(true);
    let response = null
    if(account == null || account?.accountId! == null) {
      let acctResponse = await apiCall(AuthApi.getAccount(abortSignal));
      let acct = acctResponse.success ? Account.toClass(acctResponse.data?.value) : null;
      response = await apiCall(AccountKiboshDeviceApi.getAccountKiboshDevices(acct?.accountId!, request, abortSignal));
    } else {
      response = await apiCall(AccountKiboshDeviceApi.getAccountKiboshDevices(account?.accountId!, request, abortSignal));
    }
    setLoadingAccountKiboshDevices(false);


    return response.success ? AccountKiboshDevice.toArrayOfClass(response.data?.value || []) : [];
  };

  const getAccountDeviceGroups = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(DashApi.getDeviceGroups(accountKiboshDevice!.kiboshDeviceReference!, false, abortSignal));

    return response.success ? AccountDeviceGroup.toArrayOfClass(response.data?.value || []) : [];
  };

  const getAccountDevices = async (abortSignal?: AbortSignal) => {
    if (!currentAccountKiboshDevice?.kiboshDeviceReference && !accountKiboshDevice?.kiboshDeviceReference)
      return [];
    const response = await apiCall(DashApi.getClientDevices(accountKiboshDevice?.kiboshDeviceReference ? accountKiboshDevice?.kiboshDeviceReference : currentAccountKiboshDevice?.kiboshDeviceReference!, abortSignal));

    return response.success ? AccountDevice.toArrayOfClass(response.data?.value || []) : [];
  };

  const getAccountKiboshVpnDevices = async (request?: DataSourceRequest, abortSignal?: AbortSignal) => {
    const response = await apiCall(DashApi.getAccountVpns(null, accountKiboshDevice?.kiboshDeviceReference, abortController.signal));

    return response.success ? AccountKiboshVpnDevice.toArrayOfClass(response.data?.value || []) : [];
  };

  const getDeviceTypes = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(CommonValueApi.getDeviceTypes(abortSignal));

    return response.success ? CommonValue.toArrayOfClass(response.data?.value || []) : [];
  }



  const getLastSeen = (data: any): string =>   {

    let numLength = 12 //overkill, could be 10, but let's future-proof it for centuries


    let lastSeenMoment = data.updatedAt ? moment(data.updatedAt) : null
    if(data.isVpn) {
     let v = getVpnKiboshDevice(data)
     if(v != null && v.lastDeviceCheckin != null) {
       let lastVpnMoment = moment(v.lastDeviceCheckin)
       lastSeenMoment = lastSeenMoment == null || lastSeenMoment.isBefore(lastVpnMoment) ? lastVpnMoment : lastSeenMoment
     }
    }


    let lastSeen = lastSeenMoment != null ? moment(data.updatedAt).format("X").padStart(numLength, "0") + "@" + lastSeenMoment.fromNow() : ("0").padStart(numLength, "0") + "@-"
    if( 
      data.connectionStatus != "offline" && 
      lastSeenMoment != null && 
      moment().add(-20, "minutes") < lastSeenMoment 
    ){
      lastSeen =  moment().format("X").padStart(numLength, "0") + "@" + "now"
    }
    return lastSeen;
  }



  const getStatus = (record:any, ):string => {
    let lastSeen = record.updatedAt
    let displayStatus = lastSeen.match(/now$/) || record.connectionStatus == "remote" ? record.connectionStatus : record.connectionStatus + " (since " + record.updatedAt.replace(/^.*@/, "") + ")"
    return displayStatus
  } 

  const sortDevicesByLastSeen = (devices:any) => {
    let sortedDevs = devices.sort( (a:any,b:any) => {  
      let getCmp = (d:any) => { return (d.connectionStatus == "offline" ? "0" : "1" )  + d.updatedAt ; }
      return (getCmp(b)).localeCompare( getCmp(a) ) 
    })
    return sortedDevs
  };

  const getDataAsync = async () => {
    if (!currentAccountKiboshDevice?.kiboshDeviceReference && !accountKiboshDevice?.kiboshDeviceReference)
      return [];

    setLoading(true);
    Promise.all([getAccountDeviceGroups(abortController.signal), getAccountDevices(abortController.signal), getAccountKiboshDevices({}, abortController.signal), getAccountKiboshVpnDevices({}, abortController.signal), getDeviceTypes()]).then(
      (results: any) => {
        const groups = results[0];
        let devices  = results[1];

        //parse device data

        let groupColors:any = {}
        for(let gi=0; gi < groups.length; gi++) {
          groupColors[ (groups[gi].id + "") as string ] = CONSTANTS.COLOR_SEQUENCE[gi % CONSTANTS.COLOR_SEQUENCE.length]
        }

        if (malwareDevicesOnly) {
          devices = devices.filter((x: any) => x.detectedMalware.filter((malware:any) => moment().add(-24, "hours") < moment(malware.lastQueried))?.length)
        } else if(explicitDevicesOnly) {
          devices = devices.filter((x: any) => x.detectedExplicit.filter((explicit:any) => moment().add(-24, "hours") < moment(explicit.lastQueried))?.length)
        } else {
          // filterout all devices that are not seen in last 7 days
          devices = devices.filter((x: any) => moment().add(-7, "days") < moment(x.updatedAt) || x.isVpn )
        }
        
        devices = devices.map( (d: any) => { d.updatedAt = getLastSeen(d); return d;  })

        let data:any[] = []
        groups.forEach((groupItem: AccountDeviceGroup) => {
          data.push({
            title: groupItem.name + " (Profile)",
            items: sortDevicesByLastSeen( devices.filter((x: any) => x.groupId === groupItem.id).map( (x: any) => { return {...x, groupColor: groupColors[ ("" + x.groupId) as string ]};  }) )
          })
        });

        data.push({
          title: "Un Profiled",
          items: devices.filter((x: any) => !x.groupId)
        })

        data = data.filter(x => x.items.length);

        setDataSource( data );
        setDevicesDataSource(sortDevicesByLastSeen(  data.flatMap(x => x.items.map((item: any) => { return { ...item, groupName: x.title } })) ));




        //parse vpn / kibosh device data

        let kiboshDevices = results[2];
	let vpnDevices    = results[3];
        let deviceTypes   = results[4];
        const deviceVpnType = deviceTypes?.filter((x: any) => x.value === "vpn");

        setAccountKiboshDevices(kiboshDevices)
        if (kiboshDevices.length) {
            vpnDevices = vpnDevices.map((x: any) => {
              const currentDevice = kiboshDevices.find((y: any) => y.kiboshDeviceReference === x.id);
              return {
                ...x,
                isStandalone: currentDevice?.kiboshDeviceReference ? currentDevice.isStandalone : false,
                hasActiveSubscription: currentDevice?.hasActiveSubscription || false
              };
            });
          }
          if (deviceVpnType.length) {
            vpnDevices = vpnDevices.map((x: any)=> {
              return {
                ...x,
                kiboshDeviceTypeId: deviceVpnType[0].commonValueId,
                kiboshDeviceTypeCode: deviceVpnType[0].value,
                kiboshDeviceType: deviceVpnType[0].value,
                kiboshDeviceReference: x.associatedRouterKiboshDeviceId != null ? x.associatedRouterKiboshDeviceId : x.id ,
                vpnType: x.vpnType,
                accountKiboshDeviceId: x.id,
                vpnId: x.vpnId || x.id
              }
            })
          }
          vpnDevices = vpnDevices.filter((x: any) => x.hasActiveSubscription);

          vpnDevices = vpnDevices.map((x: any, index: any)=> {
            return {
              ...x,
              kiboshDeviceName: `VPN ${index + 1}`
            }
          })
          setAccountKiboshVpnDevices(vpnDevices);
      }
    ).finally(() => {
      setLoading(false);
    });
  };


  const getVpnKiboshDevice = (accountDevice: AccountDevice) => {
    let v = accountKiboshVpnDevices.filter(x => x.accountKiboshDeviceId == accountDevice.vpnKiboshDeviceId)[0];
    return v;
  };


  const handleDeleteRequest = async () => {
    setLoading(true);
    const response = await apiCall(AccountDeviceApi.deleteAccountDevice(currentAccountDevice));
    setLoading(false);

    if (response.success) {
      message.success(`Account Device deleted successfully.`);
      setIsDeleteConfirmationPopupOpen(false);
      setReload(true);
    }
  };

  const onAccountDeviceSave = () => {
    setReload(true);
  }

  const onAccountKiboshDeviceChange = (kiboshDeviceReference: any) => {
    setCurrentAccountKiboshDevice(accountKiboshDevices.filter(x => x.kiboshDeviceReference === kiboshDeviceReference)[0]);
    setReload(true)
  }

  const onEditClick = (accountDevice: AccountDevice) => {
    setCurrentAccountDevice({ ...accountDevice });
    setPreSelectedTab("");
    setShowAccountDeviceMenuModal(true);
  };

  const onMalwareCountClick = (accountDevice: AccountDevice) => {
    if( !!(accountDevice.detectedMalware)  && accountDevice.detectedMalware?.length > 0) {
      setCurrentAccountDevice({ ...accountDevice });
      setMalwarePopupOpen(true);
    }
  };
  const onExplicitCountClick = (accountDevice: AccountDevice) => {
    if( !!(accountDevice.detectedExplicit?.length) && accountDevice.detectedExplicit?.length > 0) {
      setCurrentAccountDevice({ ...accountDevice });
      setExplicitPopupOpen(true);
    }
  };



  // const onDeleteClick = (accountDevice: AccountDevice) => {
  //   setCurrentAccountDevice(accountDevice);
  //   setIsDeleteConfirmationPopupOpen(true);
  // };

  const onSelectedTab = (accountDevice: AccountDevice, selectedTab: any) => {
    if (selectedTab) {
      setCurrentAccountDevice({ ...accountDevice });
      setPreSelectedTab(selectedTab);
      setShowAccountDeviceModal(true);
    }
  };

  const onMalwareClick = (e: any, record: any) => {
    if (!!record.detectedMalware?.length) {
      e.stopPropagation();
      onSelectedTab(record, "malware-logs");
    }
  };
  const onExplicitClick = (e: any, record: any) => {
    if (!!record.detectedExplicit?.length) {
      e.stopPropagation();
      onSelectedTab(record, "explicit-logs");
    }
  };
  const getClientIcon = (clientType: any): string => {
    let icon = "install_desktop"
    switch (clientType) {
      case "laptop":
        icon = "laptop_windows"
        break;
      case "desktop":
        icon = "computer"
        break;
      case "mobile":
        icon = "smartphone"
        break;
      case "tablet":
        icon = "tablet"
        break;
      case "security-camera":
        icon = "speed_camera"
        break;
      case "thermostat":
        icon = "thermostat"
        break;
      case "doorbell":
        icon = "doorbell"
        break;
      case "lighting":
        icon = "wb_incandescent"
        break;
      case "tv":
        icon = "tv"
        break;
      case "voip-phone":
        icon = "call"
        break;
      case "servers":
        icon = "dns"
        break;
      case "others":
        icon = "devices"
        break;
    }
    return icon;
  };

  const getMalwaresCount = (data: any): number => {
    return data.detectedMalware?.filter((malware: any) => moment().add(-24, "hours") < moment(malware.lastQueried)).length;
  }
  const getExplicitCount = (data: any): number => {
    return data.detectedExplicit?.filter((explicit: any) => moment().add(-24, "hours") < moment(explicit.lastQueried)).length;
  }


  const BlockItem: React.FC<{ data: any }> = ({ data }) => {
    return (
      <group data-direction="column" data-border="" data-radius="10" data-contain="" data-background="highlight">
        <group
          data-direction="column"
          data-space="5"
          data-adaptive="400"
	  style={{ background: data.connectionStatus == "offline" ? "#eeeeee" : "#ffffff" }}
        >
          <group data-gap="5" data-width="auto" data-align="center" data-justify="start" data-space-horizontal="5">
            <icon data-color={data.connectionStatus === "offline" ? "" : "main"} data-opacity={data.connectionStatus === "offline" ? "50" : undefined} data-icon-size="48">{getClientIcon(data.deviceType)}</icon>
          </group>
          <group data-length="autofit">
            <group data-adaptive="500">
              <group data-direction="column" data-fit="1" data-space="10" data-gap="5">
                <text data-weight="700" data-color="main" data-wrap="wrap">{data.name}</text>
                <text data-wrap="no" data-ellipsis="">
                  Device IP: {!!data.staticIp ? '-' : data.ip}
                </text>
                <text data-wrap="no" data-ellipsis="">
                  Static IP: {data.staticIp || '-'}
                </text>
                <text data-wrap="no" data-ellipsis="">
                  Connection: { getStatus(data) }
                </text>
              </group>
            </group>
            <group>
              <group
                data-align="center"
                data-position="left"
                data-width="auto"
                data-space="5"
                data-gap="5"
              >
                <group data-width="auto" data-radius="3">
                  { (!(!!data.isVpn && !!data.vpnIsConnected)) && <icon data-color="main" data-radius="3" data-space="5"  >{getDeviceSignalRateClassIcon(data.pingMs!)}</icon> }
                  {   (!!data.isVpn && !!data.vpnIsConnected)  && <img data-svg-color="main" data-radius="3" data-space="5" data-icon-size="24" data-position="center" src={ VpnSvg } /> }
                </group>
                <group data-width="auto" data-radius="3">
                  <text data-opacity={!!data.loggingEnabled ? "" : "50"} data-space="5" data-color={!!data.loggingEnabled ? "main-dark" : ""}>Logs</text>
                </group>
                <group data-width="auto" data-radius="3">
                  <text data-opacity={data.connectionStatus !== "offline" ? "" : "50"} data-space="5" data-color={data.connectionStatus !== "offline" ? "main-dark" : ""}>{data.connectionStatus}</text>
                </group>
                { !!data.isVpn &&
                  <group data-width="auto" data-radius="3">
                    <text data-opacity={!!data.vpnIsConnected ? "" : "50"} data-space="5" data-color={!!data.vpnIsConnected ? "main-dark" : "red"}>VPN</text>
                  </group>
                }
                <group data-width="auto" data-align="center" data-radius="3" data-cursor={!!getMalwaresCount(data) ? "pointer" : undefined} onClick={(e: any) => onMalwareClick(e, data)}>
                  <icon data-opacity={!!getMalwaresCount(data) ? "" : "50"} data-space-vertical="5" data-space-left='5'  data-color={!!getMalwaresCount(data) ? "red" : ""}>pest_control</icon>
                  <text data-opacity={!!getMalwaresCount(data) ? "" : "50"} data-space-vertical="5" data-space-right='5' data-color={!!getMalwaresCount(data) ? "red" : ""}>
                    : {getMalwaresCount(data)}
                  </text>
                </group>
                <group data-width="auto" data-align="center" data-radius="3" data-cursor={!!getExplicitCount(data) ? "pointer" : undefined} onClick={(e: any) => onExplicitClick(e, data)}>
                  <icon data-opacity={!!getExplicitCount(data) ? "" : "50"} data-space-vertical="5" data-space-left='5'  data-color={!!getExplicitCount(data) ? "red" : ""}>app_blocking</icon>
                  <text data-opacity={!!getExplicitCount(data) ? "" : "50"} data-space-vertical="5" data-space-right='5' data-color={!!getExplicitCount(data) ? "red" : ""}>
                    : {getExplicitCount(data)}
                  </text>
                </group>

              </group>
              <group
                data-align="center"
                data-position="right"
                data-width="auto"
                data-space="5"
              >
                <Tooltip title="Edit">
                  <div className='button micro' data-color="grey" onClick={(e: any) => { e.stopPropagation(); onEditClick(data) }}>
                    <icon>edit_square</icon>
                  </div>
                </Tooltip>
                <separator vertical="" data-adaptive="desktop"></separator>
                <ContextMenu placement="bottomLeft" items={
                  [
                    {
                      text: "Details",
                      onClick: () => onSelectedTab(data, "details")
                    },
                    {
                      text: "Filters",
                      onClick: () => onSelectedTab(data, "blocked-sites")
                    },
                    {
                      text: "Scheduler",
                      onClick: () => onSelectedTab(data, "internet-schedules")
                    },
                    {
                      text: "Activity Logs",
                      onClick: () => onSelectedTab(data, "activity-logs")
                    },
                    {
                      text: "Bandwidth Logs",
                      onClick: () => onSelectedTab(data, "bandwidth-logs")
                    }
                  ]
                }>
                  <Tooltip title="Tabs"><div className='button micro' data-color="grey" onClick={(e: any) => e.stopPropagation()}><icon>more_vert</icon></div></Tooltip>
                </ContextMenu>
              </group>
            </group>
          </group>
          <div data-position-corner="top-right" >
            <group>
            { (data.numSetFilters + data.numCustomFilters > 0 ) &&
              <icon data-color="gray" >page_info</icon>
            }
            { data.numSchedules > 0 && data.numActiveSchedules == 0 &&
              <icon data-color="gray" >schedule</icon>
            }
            { data.numSchedules > 0 && data.numActiveSchedules > 0 &&
              <icon data-icon-style="circle" data-background="red" data-color="white" >schedule</icon>
            }
            { data.isBlocked &&
              <icon data-icon-style="circle" data-background="red" data-color="white" >pause_circle</icon>
            }
            </group>
          </div>


        </group>

      </group>
    )
  };

  return (
    <>
      {loading && <Loader />}
      <view
        data-scroll=""
        data-space="20"
      >
        <group data-gap="30" data-direction="column">
          <group data-gap="10" data-direction="column">
            <text data-weight="700" data-text-size="xx-large" data-wrap="wrap" data-color="main-dark">{malwareDevicesOnly ? "Malware Affected Devices" : (explicitDevicesOnly ? "Explicit Content" :  "Devices")    }</text>
            <group>
              { malwareDevicesOnly  && <text data-wrap="wrap" data-line="1.5" data-light="">Malware affected devices</text> }
              { explicitDevicesOnly && <text data-wrap="wrap" data-line="1.5" data-light="">Devices that attempted to access explicit content</text> }
              { (!malwareDevicesOnly) && (!explicitDevicesOnly) && 
                <>
                <group data-margin-bottom="10"><text data-wrap="wrap" data-line="1.5" data-light="">All devices that have connected to your Kibosh in the last 24 hours</text></group>
                <group data-margin-bottom="10"><text data-wrap="wrap" data-line="1.5" data-light="">After being offline for over 7 days, a device will drop off the list</text></group>
                </>
              }
            </group>
          </group>
          <group data-gap="10" >
            <a style={{ color: "#0375ba"}} target="_blank" href={malwareDevicesOnly  ? "https://kibosh.com/malware-help" : (explicitDevicesOnly ? "https://kibosh.com/explicit-faq" : "/portal/dash?tab=docs") }>Help</a>
          </group>
          <group data-width="auto" data-position="left" data-align="center" data-gap="10">
            {!accountKiboshDevice?.kiboshDeviceReference && (
              <>
                <group data-width="auto" data-position="left" data-align="center" data-gap="10" data-space="10" data-radius="5" data-elevation="1" data-backdrop="">
                  <Select
                    autoComplete=""
                    label="Kibosh Device"
                    labelPosition="left"
                    dataLength="320"
                    onChange={onAccountKiboshDeviceChange}
                    loading={loadingAccountKiboshDevices}
                    allowSearch={true}
                    options={
                      accountKiboshDevices?.map((item) => {
                        return {
                          text: `${item.kiboshDeviceName!} (${item.kiboshDeviceReference!})`,
                          value: item.kiboshDeviceReference!,
                        };
                      }) || []
                    }
                    button={
                      <Button
                        material
                        icon="refresh"
                        onClick={getAccountKiboshDevicesAsync}
                      />
                    }
                  />
                </group>
                <separator data-vertical=""></separator>
              </>
            )}
            <Tooltip title="Refresh" arrow={false}>
              <group data-width="auto">
                <Button
                  secondary
                  data-length="50"
                  data-height="50"
                  data-elevation="6"
                  rounded
                  icon="refresh"
                  onClick={() => setReload(true)}
                />
              </group>
            </Tooltip>
            {
              !isMobile &&
              <Tooltip title="Advance Mode" arrow={false}>
                <group data-width="auto">
                  <Button
                    secondary
                    data-length="50"
                    data-height="50"
                    data-elevation="6"
                    rounded
                    icon="display_settings"
                    onClick={() => setAdvanceMode(!advanceMode)}
                  />
                </group>
              </Tooltip>
            }
          </group>
          {!advanceMode && dataSource.map(item => (
            <group data-gap="5">
              {dataSource.length > 1 &&
                <group data-color="main" data-space-horizontal="10">
                  <text data-weight="700">{item.title}</text>
                </group>
              }
              <ListView
                dataSource={item.items}
                view={ListViewType.Block}
                keyField="id"
                data-template="350"
                listProps={{ "data-gap": "10" }}
                itemComponent={BlockItem}
                onItemClick={onEditClick} />
            </group>
          ))}
        </group>


        {advanceMode && <view className="table_cont" data-background="none" data-margin-top="10">
          <DataTable
            columns={columns}
            bordered={true}
            customPagination={true}
            reload={reload}
            dataSource={devicesDataSource}
            loading={loading}
            rowKey={(record) => `${record.id}`}
            showTotal={true}
            size="small"
            totalRecords={devicesDataSource.length}
            onRow={(record) => ({
              onDoubleClick: () => onEditClick(record),
              style: { cursor: "pointer" },
            })}
          />
        </view>}
      </view>

      {!!showAccountDeviceModal && (
        <AccountDeviceModal
          open={showAccountDeviceModal}
          account={account!}
          accountDevice={currentAccountDevice}
          accountKiboshDevice={
            accountKiboshDevice || currentAccountKiboshDevice!
          }
          preSelectedTab={preSelectedTab}
          onClose={() => setShowAccountDeviceModal(false)}
          onSave={(e) => onAccountDeviceSave()}
          onGroupSave={() => { if (onGroupSave) onGroupSave() }}
        />
      )}
      {!!showAccountDeviceMenuModal && (
        <AccountDeviceMenuModal
          open={showAccountDeviceMenuModal}
          account={account!}
          accountDevice={currentAccountDevice}
          accountKiboshDevice={
            accountKiboshDevice || currentAccountKiboshDevice!
          }
          accountVpnKiboshDevice={  getVpnKiboshDevice(currentAccountDevice) }
          onClose={() => setShowAccountDeviceMenuModal(false)}
          onSave={() => onAccountDeviceSave()}
          onGroupSave={() => { if (onGroupSave) onGroupSave() }}
        />
      )}
      {isDeleteConfirmationPopupOpen && (
        <ConfirmationPopup
          showButton={false}
          positiveButtonText="Delete"
          positiveCallback={handleDeleteRequest}
          negativeCallback={() => {
            setIsDeleteConfirmationPopupOpen(false);
          }}
        />
      )}
      {malwarePopupOpen && 
        <Popup
          title={ "Malware Logs" }
          onCancel={ () => setMalwarePopupOpen(false)}
          onClose={ () => setMalwarePopupOpen(false)}
          noCommandbar={true}
          adaptive
          fixSize="larger"
        >
          <AccountDeviceMalwareLogs refresh={!!account?.accountId} account={account!} kiboshDeviceReference={accountKiboshDevice?.kiboshDeviceReference} deviceReference={currentAccountDevice.id} onCancel={ () => { return true; }} onSave={() => { return true; }} />
        </Popup>
      }
      {explicitPopupOpen && 
        <Popup
          title={ "Explicit Logs" }
          onCancel={ () => setExplicitPopupOpen(false)}
          onClose={ () => setExplicitPopupOpen(false)}
          noCommandbar={true}
          adaptive
          fixSize="larger"
        >
          <AccountDeviceExplicitLogs refresh={!!account?.accountId} account={account!} kiboshDeviceReference={accountKiboshDevice?.kiboshDeviceReference} deviceReference={currentAccountDevice?.id} onCancel={ () => { return true; }} onSave={() => { return true; }} />
        </Popup>
      }


    </>
  );
}
